<template>
    <button ref="btn"
        class="ath-button-v2"
        :type="buttonType"
        :disabled="disabled"
        :title="uiTooltip"
        :style="uiStyle"
        :class="[
            link !== true ? `el--s-${size}` : '',
            uiShowColor ? uiTypeClass : '',
            uiShowColor && uiHasColorVars ? 'el--color' : '',
            inline ? 'el--inline' : '',
            transparent ? 'el--transparent' : '',
            transparentUntilHover && transparent !== true && link !== true ? 'el--transparent-until-hover' : '',
            pill ? 'el--pill' : '',
            link ? 'el-button-link' : '',
            link && uiHasLinkColorVars ? 'el--link-color' : '',
            lineStyle ? `el--ls-${lineStyle}` : '',
            loading ? 'el-button-loading' : ''
        ]"
        @click="$emit('click', $event)">
        <Icon v-if="icon" :name="icon" class="sm-r-xs" />
        <slot name="before"></slot>
        <span class="o-ol" :class="labelClass" v-if="uiShowLabel">
            {{ uiLabelContent }}
        </span>
        <slot></slot>
        <slot name="after"></slot>
        <Icon v-if="iconRight" :name="iconRight" class="sm-l-xs" />
    </button>
</template>

<script>
import { isNeString } from '@aretheyhappy/utils';
import Icon from './visuals/Icon.vue';

export default {
    name: 'mead-button-v2',
    components: {Icon},
    props: {
        /**
         * Text label for the button. Passed through I18N.go for translation.
         */
        label: {type: String, default: ''},
        /**
         * Additional class(es) applied to the label span.
         */
        labelClass: {type: String, default: ''},
        /**
         * Optional number to display alongside the label in parentheses, e.g. "Label (3)".
         * Also accepts `false` to hide it.
         */
        qualifier: {type: [Number, Boolean], default: false},
        /**
         * Overrides the button's title/tooltip text. Falls back to the label when `false`.
         */
        tooltip: {type: [String, Boolean], default: false},
        /**
         * The native HTML button type attribute (e.g. "button", "submit", "reset").
         */
        buttonType: {type: String, default: 'button'},
        /**
         * Name of an icon to render before the label content.
         */
        icon: {type: [String, Boolean], default: false},
        /**
         * Name of an icon to render after the label content.
         */
        iconRight: {type: [String, Boolean], default: false},
        /**
         * Disables the button, preventing clicks and applying a disabled visual style.
         */
        disabled: {type: Boolean, default: false},
        /**
         * Renders the button with a transparent background.
         */
        transparent: {type: Boolean, default: false},
        /**
         * Renders the button with a transparent background until it is hovered, focused
         * with the keyboard or pressed, then shows its `colorStyle` colors. Ignored when
         * `transparent` or `link` is set.
         */
        transparentUntilHover: {type: Boolean, default: false},
        /**
         * Renders the button styled as a text link rather than a standard button. The link takes
         * its text and underline color from `color` or a non-default `colorStyle`.
         */
        link: {type: Boolean, default: false},
        /**
         * Style of the button outline, or of the underline when `link` is set. One of "solid", "dashed",
         * "dotted" or "none". The outline is drawn in the button's text color. When empty, buttons have
         * no outline and links a solid underline.
         */
        lineStyle: {
            type: String,
            default: '',
            validator: value => ['', 'solid', 'dashed', 'dotted', 'none'].includes(value),
        },
        /**
         * Renders the button with pill (rounded) styling.
         */
        pill: {type: Boolean, default: false},
        /**
         * Size of the button. One of BUTTON_SIZES (xs, s, m, l).
         */
        size: {type: String, default: () => MeadCommon.BUTTON_SIZES.MEDIUM},
        /**
         * Deprecated: use `color` instead, which takes the same names plus tints and any CSS color.
         *
         * Color style/theme of the button. One of BUTTON_COLOR_STYLES. Also accepts any COLOR_STYLES
         * value or a tint of one (e.g. "grade-a", "primary-20").
         * @deprecated Use `color` instead.
         */
        colorStyle: {type: String, default: () => MeadCommon.BUTTON_COLOR_STYLES.DEFAULT},
        /**
         * Color of the button: a COLOR_STYLES value (e.g. "primary"), a tint of one (e.g. "primary-20")
         * or any CSS color (e.g. "#BAC3C4"). Overrides `colorStyle` when set.
         */
        color: {type: String, default: ''},
        /**
         * Renders the button inline (inline-flex, auto width) instead of full width.
         */
        inline: {type: Boolean, default: true},
        /**
         * If true, the button will automatically receive focus when mounted.
         */
        uiAutoFocus: {type: Boolean, default: false},
        /**
         * If true, displays the button in a loading state and disables its content.
         */
        loading: {type: Boolean, default: false}
    },
    computed: {
        //  Colors are hidden for transparent and link buttons
        uiShowColor: function () {
            return this.transparent !== true && this.link !== true;
        },
        //  BUTTON_COLOR_STYLES keep their el--t-* class rules; anything else is drawn from uiColorVars
        uiTypeClass: function () {
            if (!isNeString(this.color)) return `el--t-${this.colorStyle}`;
            return Object.values(MeadCommon.BUTTON_COLOR_STYLES).includes(this.color) ? `el--t-${this.color}` : '';
        },
        uiColorVars: function () {
            const names = {bg: '--button-bg', fg: '--button-fg'};
            if (isNeString(this.color)) {
                return Object.values(MeadCommon.BUTTON_COLOR_STYLES).includes(this.color)
                    ? {}
                    : MeadCommon.colorVars(this.color, names);
            }
            return Object.values(MeadCommon.BUTTON_COLOR_STYLES).includes(this.colorStyle)
                ? {}
                : MeadCommon.colorVars(this.colorStyle, names, {allowCustom: false});
        },
        uiHasColorVars: function () {
            return Object.keys(this.uiColorVars).length > 0;
        },
        //  Links draw their text and underline from the color itself. The default colorStyle keeps the plain link look
        uiLinkColorVars: function () {
            const names = {bg: '--button-link'};
            if (isNeString(this.color)) return MeadCommon.colorVars(this.color, names);
            if (this.colorStyle === MeadCommon.BUTTON_COLOR_STYLES.DEFAULT) return {};
            return MeadCommon.colorVars(this.colorStyle, names, {allowCustom: false});
        },
        uiHasLinkColorVars: function () {
            return Object.keys(this.uiLinkColorVars).length > 0;
        },
        uiStyle: function () {
            if (this.link === true) return this.uiLinkColorVars;
            return this.uiShowColor ? this.uiColorVars : null;
        },
        uiTooltip: function () {
            return this.tooltip !== false && isNeString(this.tooltip) ? this.tooltip : this.label;
        },
        uiShowLabel: function () {
            return this.uiLabelContent.length > 0;
        },
        uiLabel: function () {
            return MeadCommon.I18N.go(this.label);
        },
        uiLabelContent: function () {
            return `${this.uiLabel}${ this.qualifier || this.qualifier === 0 ? ' (' + this.qualifier + ') ' : '' }`;
        }
    },
    mounted () {
        //  UIAutoFocus
        if (this.$refs.btn && this.uiAutoFocus) this.$refs.btn.focus();
    },
};
</script>
<style lang="scss">
@import '../../../sass/_variables.scss';
.ath-button-v2 {
    //  Overlay drawn over the background on hover and press, so every color style reacts the same way
    --button-overlay-hover: rgba(0, 0, 0, .12);
    --button-overlay-active: rgba(0, 0, 0, .2);

    display: flex;
    width: 100%;
    max-width: 100%;
    font-family: $ff-b;
    border-radius: $br-m;
    line-height: 1;
    align-items: center;
    justify-content: center;
    border: none;
    box-shadow: inset 0 0 0 100vmax transparent;
    cursor: pointer;
    font-weight: bold;
    text-align: center;
    font-size: $fs-m;
    pointer-events: initial;
    transition: border-color .2s ease, background-color .2s ease, color .2s ease, box-shadow .2s ease;

    .ath-dark-theme & {
        --button-overlay-hover: rgba(255, 255, 255, .12);
        --button-overlay-active: rgba(255, 255, 255, .2);
    }

    &:not(.el-button-link) {
        background-color: #efefef;
    }

    * {
        pointer-events: none;
    }

    &[disabled="disabled"] {
        cursor: not-allowed;
        opacity: .5;
    }

    &:focus {
        outline: none;
    }

    &:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }

    //  Inset shadow tints the background without touching the text, and transitions both ways
    &:not([disabled="disabled"]):not(.el-button-loading):not(.el-button-link) {
        &:hover {
            box-shadow: inset 0 0 0 100vmax var(--button-overlay-hover);
        }

        &:active {
            box-shadow: inset 0 0 0 100vmax var(--button-overlay-active), var(--c-shade-m);
        }
    }

    //  MODIFIERS

    &.el--inline {
        display: inline-flex;
        width: auto;
    }

    &.el--transparent {
        background-color: transparent;
        color: var(--c-txt);
    }

    // Keeps the el--t-* colors, but hides them until hover, keyboard focus or press
    &.el--transparent-until-hover:not(:hover):not(:focus-visible):not(:active),
    &.el--transparent-until-hover[disabled="disabled"] {
        background-color: transparent;
        color: var(--c-txt);
    }

    @each $style in (solid, dashed, dotted, none) {
        &.el--ls-#{$style} {
            &:not(.el-button-link) {
                border: 1px $style currentColor;
            }

            &.el-button-link {
                border-bottom-style: $style;
            }
        }
    }

    &.el--pill {
        font-weight: 400;
        border-radius: $br-m;
    }

    &.el-button-link {
        border-radius: 0;
        font-size: $fs-s;
        font-weight: 400;
        background-color: transparent;
        border-bottom: 1px solid var(--c-txt);

        &:not([disabled="disabled"]):active,
        &:not([disabled="disabled"]):hover {
            &:not(.el-button-loading) {
                color: var(--c-txt-link);
                border-bottom-color: var(--c-txt-link);
            }
        }

        // Color from the `color` or `colorStyle` prop, set as --button-link
        &.el--link-color,
        &.el--link-color:not([disabled="disabled"]):active:not(.el-button-loading),
        &.el--link-color:not([disabled="disabled"]):hover:not(.el-button-loading) {
            color: var(--button-link);
            border-bottom-color: var(--button-link);
        }

        &.el-button-loading {
            cursor: default;
            border-bottom-color: transparent;
        }
    }


    //  SIZES

    &.el--s-xs {
        height: 2rem;
        line-height: 2rem;
        font-size: $fs-s;
        padding: 0 $s-xs;
    }

    &.el--s-s {
        height: 3rem;
        line-height: 3rem;
        font-size: $fs-s;
        padding: 0 $s-s;
    }

    &.el--s-m {
        height: 4rem;
        line-height: 4rem;
        font-size: $fs-s;
        padding: 0 $s-l;
    }

    &.el--s-l {
        height: 5rem;
        line-height: 5rem;
        font-size: $fs-m;
        padding: 0 $s-l;
    }

    //  TYPES

    @mixin elBtnType ($name, $bg, $fg) {
        &.el--t-#{$name} {
            background-color: $bg;
            color: $fg;
        }
    }

    @include elBtnType('primary',   var(--c-brand-primary-bg), var(--c-brand-primary-fg));
    @include elBtnType('secondary', var(--c-brand-secondary-bg), var(--c-brand-secondary-fg));
    @include elBtnType('default',   var(--c-brand-default-bg), var(--c-brand-default-fg));
    @include elBtnType('active',    var(--c-brand-active-bg), var(--c-brand-active-fg));
    @include elBtnType('warning',   var(--c-brand-warning-bg), var(--c-brand-warning-fg));
    @include elBtnType('danger',   var(--c-brand-danger-bg), var(--c-brand-danger-fg));
    @include elBtnType('info',      var(--c-brand-info-bg), var(--c-brand-info-fg));
    @include elBtnType('dark',      var(--c-brand-dark-bg), var(--c-brand-dark-fg));
    @include elBtnType('light',     var(--c-brand-light-bg), var(--c-brand-light-fg));

    // Any other color from the `color` or `colorStyle` prop, set as --button-bg/--button-fg
    &.el--color {
        background-color: var(--button-bg);
        color: var(--button-fg, var(--c-txt));
    }

    // Loading state
    &.el-button-loading {
        font-size: 0 !important;
        position: relative;
        display: block;
        min-width: 3rem;

        i,
        span,
        svg {
            opacity: 0 !important;
        }

        &:after {
            display: inline-block;
            width: 2rem;
            height: 2rem;
            position: absolute;
            top: 50%;
            left: 50%;
            margin-left: -$s-s;
            margin-top: -$s-s;
            content: '';
            background-color: transparent;
            border-radius: 100%;
            animation: rotateFull .75s linear infinite;
            pointer-events: none;
            border: .2rem solid transparent;
            border-left-color: var(--c-brand-dark-bg);
            border-top-color: var(--c-brand-dark-bg);
            border-style: solid;
        }
    }
}
</style>